import { useEffect, useMemo, useState } from "react";
import { api, type Site } from "../../lib/api";
import type { AdminBuild } from "../../lib/api/admin";

export function mergeBuilds(engine: AdminBuild[], bundles: AdminBuild[]): AdminBuild[] {
  const map = new Map(engine.map(b => [b.id, b]));
  for (const bundle of bundles) {
    const job = map.get(bundle.id);
    map.set(bundle.id, job ? { ...job, previewUrl: bundle.previewUrl ?? job.previewUrl, siteId: job.siteId ?? bundle.siteId,
      subdomain: job.subdomain ?? bundle.subdomain, name: job.name === "Untitled build" ? bundle.name : job.name } : bundle);
  }
  return [...map.values()].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0) || a.id.localeCompare(b.id));
}

export function useAdminInventory() {
  const [revision, setRevision] = useState(0);
  const [sites, setSites] = useState<Site[]>([]);
  const [engine, setEngine] = useState<AdminBuild[]>([]);
  const [bundles, setBundles] = useState<AdminBuild[]>([]);
  const [loading, setLoading] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    const labels = { sites: "Sites", engine: "Build records", bundles: "Published previews" };
    setSites([]); setEngine([]); setBundles([]); setErrors([]); setLoading(Object.values(labels));
    const run = async (label: string, work: () => Promise<void>) => {
      try { await work(); }
      catch { if (!controller.signal.aborted) setErrors(prev => [...prev, `${label} could not be loaded. Refresh to retry; the listing is incomplete.`]); }
      finally { if (!controller.signal.aborted) setLoading(prev => prev.filter(item => item !== label)); }
    };
    const allPages = async (source: "engine" | "bundles", append: typeof setEngine) => {
      let cursor: string | undefined;
      const seen = new Set<string>();
      do {
        const page = await api.adminBuilds(source, cursor, controller.signal);
        if (controller.signal.aborted) return;
        append(prev => [...prev, ...page.builds]);
        cursor = page.nextCursor ?? undefined;
        if (cursor && seen.has(cursor)) throw new Error("Repeated inventory cursor");
        if (cursor) seen.add(cursor);
      } while (cursor);
    };
    void Promise.all([
      run(labels.sites, async () => { const result = await api.listSites(); if (!controller.signal.aborted) setSites(result.sites); }),
      run(labels.engine, () => allPages("engine", setEngine)),
      run(labels.bundles, () => allPages("bundles", setBundles)),
    ]);
    return () => controller.abort();
  }, [revision]);
  const builds = useMemo(() => mergeBuilds(engine, bundles), [engine, bundles]);
  return { sites, builds, loading, errors, refresh: () => setRevision(value => value + 1) };
}
